import React, { useState, useEffect, useMemo } from 'react';
import styles from './css/Login.module.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import '../../../node_modules/font-awesome/css/font-awesome.min.css';
import { isObject } from '../../utils/commonUtils';
import { useDispatch } from 'react-redux';
import { GetProjectData, selectProject, setProjectImg } from '../../redux/projectDataSlice';
import store from '../../redux/store';
import { GetMyWorkspaces, GetUserData, logout, logoutWithAudit, setAccessToken, setWorkspaceSelectionRequired } from '../../redux/usersSlice';
import AuthService from '../../apiServices/tokenservices/auth.service';
import { initiateAzureLogin } from '../../apiServices/ssoService';
import Cookies from 'js-cookie';
import { Eye16Regular, EyeOff16Regular, SpinnerIos16Regular } from '@fluentui/react-icons';
import { setDocs } from '../../redux/documentsSlice';
import Logo from '../../assets/longLogoBright.png';
import { writePendingSsoMfaState, readPendingSsoMfaState, clearPendingSsoMfaState, hasPendingSsoMfaState } from '../../utils/pendingSsoMfa';
import { getProjectHomePath } from '../../utils/projectHomePath';
import { getReturnPathFromLocation, clearPostAuthReturn } from '../../utils/postAuthRedirect';
import { openCookiePreferences } from '../../utils/openCookiePreferences';
import {
    stashInviteKeyFromSearch,
    consumePendingInviteKey,
    peekPendingInviteKey,
    setRouteAfterLoginInviteLock,
    hasRouteAfterLoginInviteLock,
} from '../../utils/pendingInviteKey';

// Guard to prevent duplicate Microsoft SSO success toasts when effects run multiple times (e.g. React StrictMode)
let azureSSOSuccessToastShown = false;
const SHARED_DOC_PARAM_KEY = 'shared_doc_id';
const SUPPORT_EMAIL = 'LensSupport@beyond-ma.com';

function buildSetup2faUrl(search) {
    const p = new URLSearchParams();
    const sp = new URLSearchParams(search);
    const doc = sp.get('doc');
    let invite = sp.get('invite');
    // Microsoft OAuth returns ?success/&access_token=… without ?invite= — read stashed key (set before SSO redirect).
    if (!invite) {
        invite = peekPendingInviteKey();
    }
    if (doc) {
        p.set('doc', doc);
    }
    if (invite) {
        p.set('invite', invite);
    }
    const s = p.toString();
    return s ? `/setup-2fa?${s}` : '/setup-2fa';
}

function Login() {
    const location = useLocation();
    /** Prevents duplicate SSO-link session teardown if this effect runs twice (e.g. Strict Mode). */
    const azureLinkSwitchCleanupRef = React.useRef(false);
    const [rememberMe, setRememberMe] = React.useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [loginLoading, setLoginLoading] = React.useState(false);
    const [microsoftLoading, setMicrosoftLoading] = React.useState(false);
    const [showpassword, setShowpassword] = useState(false);
    const [lockoutSecondsRemaining, setLockoutSecondsRemaining] = useState(0);
    const [showLockoutScreen, setShowLockoutScreen] = useState(false);
    const [showPermanentLockScreen, setShowPermanentLockScreen] = useState(false);
    const previousLockoutSecondsRef = React.useRef(lockoutSecondsRemaining);
    const [formData, setFormData] = React.useState({
        email: '',
        password: '',
    });
    const [resumingSession, setResumingSession] = React.useState(Boolean(Cookies.get('refresh')));
    const isAccountLocked = lockoutSecondsRemaining > 0;
    const shouldShowLockout = showLockoutScreen || isAccountLocked;

    useEffect(() => {
        if (!isAccountLocked) {
            return undefined;
        }

        const intervalId = window.setInterval(() => {
            setLockoutSecondsRemaining(prev => (prev > 0 ? prev - 1 : 0));
        }, 1000);

        return () => window.clearInterval(intervalId);
    }, [isAccountLocked]);

    const lockoutMinutesRemaining = Math.floor(lockoutSecondsRemaining / 60);
    const lockoutSecondsDisplay = lockoutSecondsRemaining % 60;
    const lockoutCountdownText = `${lockoutMinutesRemaining}:${String(lockoutSecondsDisplay).padStart(2, '0')}`;

    useEffect(() => {
        const previousSeconds = previousLockoutSecondsRef.current;
        const lockoutJustExpired = previousSeconds > 0 && lockoutSecondsRemaining === 0 && showLockoutScreen && !showPermanentLockScreen;

        if (lockoutJustExpired) {
            toast.success('Your temporary sign-in restriction has been lifted. Please continue to sign in.', {
                autoClose: 10000,
            });
        }

        previousLockoutSecondsRef.current = lockoutSecondsRemaining;
    }, [lockoutSecondsRemaining, showLockoutScreen, showPermanentLockScreen]);

    const routeAfterLogin = React.useCallback(async () => {
        // If we already handed off to /validate-invite from this login (e.g. OAuth + duplicate effects),
        // do not fall through to select-workspace on a second routeAfterLogin call.
        if (hasRouteAfterLoginInviteLock()) {
            return;
        }

        // Prefer sessionStorage (set when landing with ?doc=); fallback to current URL in case it was lost
        const docFromUrl = new URLSearchParams(location.search).get('doc');
        let sharedDocId = sessionStorage.getItem(SHARED_DOC_PARAM_KEY) || docFromUrl;
        if (docFromUrl && !sessionStorage.getItem(SHARED_DOC_PARAM_KEY)) {
            sessionStorage.setItem(SHARED_DOC_PARAM_KEY, docFromUrl);
        }

        stashInviteKeyFromSearch(location.search);

        // Shared document + project invite: handle before GetMyWorkspaces (same as email/password and SSO).
        // Otherwise a second routeAfterLogin after OAuth can see multi-workspace + no active id and send users to select-workspace.
        if (sharedDocId) {
            sessionStorage.removeItem(SHARED_DOC_PARAM_KEY);
            navigate(`/shared/document/${sharedDocId}`, {
                replace: true,
                state: { fallbackPath: '/project' },
            });
            return;
        }

        const pendingInviteKey = consumePendingInviteKey();
        if (pendingInviteKey) {
            setRouteAfterLoginInviteLock(pendingInviteKey);
            navigate(`/validate-invite/${pendingInviteKey}`, {
                replace: true,
                state: { fallbackPath: '/project' },
            });
            return;
        }

        const returnPath = getReturnPathFromLocation(location);
        if (returnPath) {
            clearPostAuthReturn();
            navigate(returnPath, { replace: true });
            return;
        }

        const res = await dispatch(GetMyWorkspaces());
        const memberships = res?.payload?.data?.memberships || [];

        if (!memberships.length) {
            navigate('/no-workspace-access', { replace: true });
            return;
        }

        const { activeWorkspaceId: activeFromStore, user: userFromStore } = store.getState().user;
        const activeWorkspaceId = activeFromStore || localStorage.getItem('active_workspace_id');

        if (memberships.length > 1 && !activeWorkspaceId) {
            dispatch(setWorkspaceSelectionRequired(true));
            navigate('/select-workspace', { replace: true });
            return;
        }

        dispatch(setWorkspaceSelectionRequired(false));

        const projectId = localStorage.getItem('project_id');
        if (projectId) {
            try {
                const gpRes = await dispatch(GetProjectData()).unwrap();
                const projects = gpRes?.data ?? store.getState().projectData?.data;
                const currentProject = Array.isArray(projects) ? projects.find(p => String(p.id) === String(projectId)) : null;
                if (currentProject) {
                    dispatch(selectProject({ id: currentProject.id }));
                    const path = getProjectHomePath(currentProject, userFromStore);
                    navigate(path, { replace: true });
                    return;
                }
            } catch {
                // Stale project id or load error — fall back to projects list
            }
        }

        navigate('/project', { replace: true });
    }, [dispatch, navigate, location.search, location.state]);

    React.useEffect(() => {
        stashInviteKeyFromSearch(location.search);
    }, [location.search]);

    const processingAuthRedirect = useMemo(() => {
        const p = new URLSearchParams(location.search);
        if (p.has('success') || p.has('access_token') || p.has('mfa_required') || p.has('error')) {
            return true;
        }
        if (p.get('linked') === 'success' && p.get('method_switched') === 'azure_sso') {
            return true;
        }
        return false;
    }, [location.search]);

    // Already logged in: leave login screen and use the same routing as after a fresh sign-in
    useEffect(() => {
        if (processingAuthRedirect) {
            return;
        }
        if (!Cookies.get('refresh')) {
            return;
        }

        const pendingReturnPath = getReturnPathFromLocation(location);
        let cancelled = false;
        setResumingSession(true);

        (async () => {
            try {
                const res = await dispatch(GetUserData()).unwrap();
                if (cancelled) {
                    return;
                }
                const userData = res?.data?.[0];
                if (!userData?.id) {
                    setResumingSession(false);
                    return;
                }
                clearPendingSsoMfaState();
                if (!userData.has_mfa) {
                    setResumingSession(false);
                    navigate(buildSetup2faUrl(location.search), { replace: true });
                    return;
                }
                if (pendingReturnPath) {
                    clearPostAuthReturn();
                    navigate(pendingReturnPath, { replace: true });
                    return;
                }
                await routeAfterLogin();
            } catch {
                setResumingSession(false);
                // Invalid or expired session — stay on login; axios may clear auth elsewhere
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [processingAuthRedirect, dispatch, navigate, routeAfterLogin, location.search, location.state]);

    // Handle Azure SSO redirect responses
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);

        stashInviteKeyFromSearch(location.search);

        const sharedDocId = urlParams.get('doc');
        if (sharedDocId) {
            sessionStorage.setItem(SHARED_DOC_PARAM_KEY, sharedDocId);
        }

        // Check for method switching redirect
        const linked = urlParams.get('linked');
        const methodSwitched = urlParams.get('method_switched');

        if (linked === 'success' && methodSwitched === 'azure_sso') {
            // Backend switched auth_method to azure_sso but the browser still has the old email/password
            // session. If we only strip query params, "already logged in" runs and routeAfterLogin() fights
            // the intended Microsoft sign-in (flicker / redirect loop). End the session first, then land on clean login.
            if (azureLinkSwitchCleanupRef.current) {
                return;
            }
            azureLinkSwitchCleanupRef.current = true;
            toast.success('Microsoft account linked! Please sign in with Microsoft SSO.', { toastId: 'linked-azure-sso' });
            void (async () => {
                try {
                    await dispatch(logoutWithAudit());
                } catch {
                    // still clear client session
                }
                Cookies.set('refresh', '');
                dispatch(setAccessToken(''));
                clearPendingSsoMfaState();
                dispatch(setProjectImg(''));
                dispatch(setDocs([]));
                dispatch(setWorkspaceSelectionRequired(false));
                dispatch(logout());
                navigate('/login', { replace: true });
            })();
            return;
        }

        // Check for success case (direct login without MFA)
        const success = urlParams.get('success');
        const accessToken = urlParams.get('access_token');
        const refreshToken = urlParams.get('refresh_token');

        if (success === 'true' && accessToken && refreshToken) {
            // Azure SSO login successful - set tokens and navigate
            dispatch(setAccessToken(accessToken));
            Cookies.set('refresh', refreshToken, {
                secure: true,
                sameSite: 'strict',
            });
            clearPendingSsoMfaState();
            dispatch(GetUserData()).then(res => {
                const userData = res?.payload?.data?.[0];
                if (userData && !userData.has_mfa) {
                    navigate(buildSetup2faUrl(location.search), { replace: true });
                    if (!azureSSOSuccessToastShown) {
                        azureSSOSuccessToastShown = true;
                        toast.success('Please set up 2FA to secure your account');
                    }
                    return;
                }
                routeAfterLogin();
                if (!azureSSOSuccessToastShown) {
                    azureSSOSuccessToastShown = true;
                    toast.success('Successfully logged in with Microsoft');
                }
            });
            return;
        }

        // Check for MFA required case
        const mfaRequired = urlParams.get('mfa_required');
        const ephemeralToken = urlParams.get('ephemeral_token');
        const method = urlParams.get('method');

        if (mfaRequired === 'true' && ephemeralToken) {
            // This is a redirect from Azure SSO that requires MFA
            const mfaState = {
                ephemeral_token: ephemeralToken,
                type: method,
                email: '',
                password: '',
            };
            writePendingSsoMfaState(mfaState);
            navigate('/authenticate', {
                state: mfaState,
                replace: true, // Replace the current URL to clear parameters
            });
            return;
        }

        // Check for error cases
        const error = urlParams.get('error');
        if (error) {
            // Handle Azure SSO errors
            let errorMessage = 'Login failed';

            switch (error) {
                case 'user_not_found':
                    errorMessage = 'No account found with this email. Please contact your administrator.';
                    break;
                case 'account_not_linked':
                    errorMessage = 'Microsoft account not linked. Please link your account first.';
                    break;
                case 'account_mismatch':
                    errorMessage = 'Azure account mismatch. Please contact support.';
                    break;
                case 'email_missing':
                    errorMessage = 'Email not found in Azure response.';
                    break;
                case 'azure_token_error':
                    const reason = urlParams.get('reason');
                    errorMessage = `Azure authentication error: ${reason || 'Unknown error'}`;
                    break;
                default:
                    errorMessage = decodeURIComponent(error);
            }

            toast.error(errorMessage);

            // Clear OAuth params but keep project-invite deep link when possible (SSO error recovery).
            const inv = peekPendingInviteKey();
            navigate(inv ? `/login?invite=${encodeURIComponent(inv)}` : '/login', { replace: true });
        }
    }, [location.search, navigate, dispatch, routeAfterLogin]);

    const handleSubmit = async e => {
        e.preventDefault();
        if (isAccountLocked || showPermanentLockScreen) {
            return;
        }

        setLoginLoading(true);
        try {
            const res = await AuthService.login(formData?.email?.toLowerCase(), formData?.password, rememberMe);
            if (res?.access) {
                // Direct login (no MFA or MFA bypassed): set token, load user + workspaces, then redirect
                clearPendingSsoMfaState();
                dispatch(setAccessToken(res?.access));
                Cookies.set('refresh', res?.refresh, {
                    secure: true,
                    sameSite: 'strict',
                });
                const userRes = await dispatch(GetUserData());
                const userData = userRes?.payload?.data?.[0];
                setLoginLoading(false);
                if (userData && !userData.has_mfa) {
                    navigate(buildSetup2faUrl(location.search), { replace: true });
                    toast.success('Please set up 2FA to secure your account');
                    return;
                }
                await routeAfterLogin();
                toast.success('Success');
            } else if (res?.ephemeral_token) {
                setLoginLoading(false);
                const mfaState = {
                    ephemeral_token: res?.ephemeral_token,
                    type: res?.method,
                    email: formData?.email,
                    password: formData?.password,
                };
                writePendingSsoMfaState(mfaState);
                navigate('/authenticate', {
                    state: mfaState,
                });
            } else {
                setLoginLoading(false);
            }
        } catch (err) {
            setLoginLoading(false);
            const resp = err?.response?.data;
            const statusCode = err?.response?.status;
            const responseMessage = String(resp?.error || resp?.detail || '').toLowerCase();
            const isPermanentLockedResponse = statusCode === 403 && (resp?.login_permanently_locked === true || responseMessage.includes('locked'));

            if (isPermanentLockedResponse) {
                setShowPermanentLockScreen(true);
                setShowLockoutScreen(false);
                setLockoutSecondsRemaining(0);
                toast.error(resp?.error || 'This account has been locked. Contact support.');
                return;
            }

            if (isObject(resp)) {
                const isInvalidCredentials =
                    responseMessage.includes('invalid credentials') ||
                    responseMessage.includes('no active account found') ||
                    responseMessage.includes('email or password');

                if (isInvalidCredentials) {
                    toast.error('The email address or password entered is incorrect. Please try again.');
                    return;
                }

                if (err?.response?.status === 423) {
                    const seconds = Number(resp?.retry_after_seconds);
                    const remaining = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 5 * 60;
                    setLockoutSecondsRemaining(remaining);
                    setShowLockoutScreen(true);
                    setShowPermanentLockScreen(false);
                    toast.error('For your security, please wait a few seconds before trying again.', {
                        autoClose: 10000,
                    });
                    return;
                }
                // Handle authentication method errors
                if (resp.error && resp.auth_method) {
                    if (resp.auth_method === 'azure_sso') {
                        toast.error(<span>Email/password login is disabled. Please sign in with Microsoft SSO.</span>);
                    } else {
                        toast.error(<span>{resp.error}</span>);
                    }
                } else {
                    Object.entries(resp).forEach(([key, value]) => {
                        toast.error(
                            <span
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: '20px',
                                }}>
                                Error : <pre> </pre>
                                {key !== 'error' && <span style={{ textTransform: 'capitalize' }}>{String(key)}, </span>}
                                {String(value)}
                            </span>,
                        );
                    });
                }
            }
        }
    };

    const handleTryAgain = () => {
        if (isAccountLocked) {
            return;
        }
        setShowLockoutScreen(false);
        setShowPermanentLockScreen(false);
        setLockoutSecondsRemaining(0);
        setFormData({
            email: '',
            password: '',
        });
    };

    const handleMicrosoftLogin = async () => {
        try {
            setMicrosoftLoading(true);
            // Persist shared doc + project-invite before Azure redirect (callbacks often drop query params).
            const docParam = new URLSearchParams(location.search).get('doc');
            if (docParam) sessionStorage.setItem(SHARED_DOC_PARAM_KEY, docParam);
            stashInviteKeyFromSearch(location.search);

            const response = await initiateAzureLogin();

            if (response?.azure_auth_url) {
                // Redirect to Azure login - the backend will redirect back to login page with response parameters
                window.location.href = response.azure_auth_url;
            } else {
                toast.error('Failed to get Microsoft login URL');
                setMicrosoftLoading(false);
            }
        } catch (error) {
            console.error('Microsoft login error:', error);
            const errorMessage = error.response?.data?.error || 'Failed to initiate Microsoft login';
            toast.error(errorMessage);
            setMicrosoftLoading(false);
        }
    };

    const handleChange = e => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    /** Clear stale client session on /login without calling the API — no refresh cookie means server audit logout is useless (endpoint is IsAuthenticated). */
    const resetStaleClientSessionOnLogin = React.useCallback(() => {
        clearPendingSsoMfaState();
        localStorage.removeItem('persist:root');
        Cookies.set('refresh', '');
        dispatch(setProjectImg(''));
        dispatch(setDocs([]));
        dispatch(setWorkspaceSelectionRequired(false));
        dispatch(logout());
    }, [dispatch]);

    const handleResumePendingMfa = React.useCallback(() => {
        const pending = readPendingSsoMfaState();
        if (!pending?.ephemeral_token) {
            return;
        }
        navigate('/authenticate', { state: pending });
    }, [navigate]);

    React.useLayoutEffect(() => {
        const hasRefresh = Cookies.get('refresh');
        const hasDocParam = new URLSearchParams(location.search).has('doc');
        const pendingMfa = hasPendingSsoMfaState();
        // When coming in with ?doc=<id> (shared link), don't immediately redirect away.
        // When MFA is in progress (e.g. browser back from /authenticate), do not clear session — that would wipe state.
        // When Azure redirects with ?mfa_required=…, sessionStorage is not written until the next effect runs — skip until then.
        if (processingAuthRedirect) {
            return;
        }
        if (!hasRefresh && !hasDocParam && !pendingMfa) {
            resetStaleClientSessionOnLogin();
        }
    }, [resetStaleClientSessionOnLogin, location.search, processingAuthRedirect]);

    return (
        <div className={styles.body}>
            <div className={styles.dotedBg}>
                <div className={styles.formCard}>
                    <div className={styles.formHeader}>
                        <img src={Logo} width={73} alt="lens-logo" />
                    </div>
                    <div className={styles.formBody}>
                        {resumingSession ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '32px 0' }}>
                                <SpinnerIos16Regular className="rotateLoader" />
                                <p style={{ margin: 0, color: 'var(--Brand-Primary)', fontFamily: 'Inter', fontSize: 14 }}>
                                    Restoring your session...
                                </p>
                            </div>
                        ) : showPermanentLockScreen ? (
                            <div className={styles.lockedContainer}>
                                <h5 className={styles.lockedTitle}>Account Locked</h5>
                                <p className={styles.lockedMessage}>Your account is currently locked due to repeated unsuccessful sign-in attempts.</p>
                                <div className={styles.lockedActions}>
                                    <a
                                        href={`mailto:${SUPPORT_EMAIL}`}
                                        className={styles.submitBtn}
                                        style={{ textAlign: 'center', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
                                        Contact Support
                                    </a>
                                    {/* <button type="button" onClick={handleTryAgain} className={styles.tryAgainBtn}>
                                        Back to sign in
                                    </button> */}
                                </div>
                            </div>
                        ) : shouldShowLockout ? (
                            <div className={styles.lockedContainer}>
                                <h5 className={styles.lockedTitle}>Your Account Is Temporarily Locked</h5>
                                <p className={styles.lockedMessage}>
                                    For security reasons, your account has been temporarily locked after multiple unsuccessful sign-in attempts. Please try again in{' '}
                                    {lockoutCountdownText} or reset your password to continue.
                                </p>
                                <div className={styles.lockedActions}>
                                    <button type="button" onClick={() => navigate('/forgot-password')} className={styles.submitBtn}>
                                        Reset Password
                                    </button>
                                    {isAccountLocked ? (
                                        <a href={`mailto:${SUPPORT_EMAIL}`} className={styles.tryAgainBtn}>
                                            Contact Support
                                        </a>
                                    ) : (
                                        <button type="button" onClick={handleTryAgain} className={styles.tryAgainBtn}>
                                            Try Again
                                        </button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <>
                                {hasPendingSsoMfaState() && (
                                    <div className={styles.mfaResumeBanner}>
                                        <div className={styles.mfaResumeTitle}>Continue sign-in</div>
                                        <p className={styles.mfaResumeText}>
                                            You have a verification step in progress. If you used the browser back button, use the button below to return to 2FA.
                                        </p>
                                        <button type="button" onClick={handleResumePendingMfa} className={styles.submitBtn}>
                                            Continue to verification
                                        </button>
                                    </div>
                                )}
                                <h5 className={styles.formLabel}>Sign in</h5>
                                <form onSubmit={handleSubmit}>
                                    <div>
                                        <div>
                                            <p className={styles.fieldLabel}>Email address</p>
                                            <input value={formData?.email} name="email" onChange={handleChange} type="email" className={'form-control ' + styles.inputField} />
                                        </div>
                                        <div>
                                            <p className={styles.fieldLabel}>Password</p>
                                            <div className="input-field">
                                                <input
                                                    value={formData?.password}
                                                    name="password"
                                                    type={showpassword ? 'text' : 'password'}
                                                    onChange={handleChange}
                                                    className={'form-control ' + styles.inputField}
                                                />
                                                {showpassword ? (
                                                    <Eye16Regular onClick={() => setShowpassword(!showpassword)} className="icon icon-right svg-icons" />
                                                ) : (
                                                    <EyeOff16Regular onClick={() => setShowpassword(!showpassword)} className="icon icon-right svg-icons" />
                                                )}
                                            </div>{' '}
                                        </div>
                                        <div onClick={() => setRememberMe(!rememberMe)} role="button" className={styles.rememberMeSection}>
                                            <input checked={rememberMe} type="checkbox" />
                                            <label className={styles.rememberBeText}>Stay signed in</label>
                                        </div>
                                        <div className="d-flex align-items-center justify-content-between">
                                            <button disabled={loginLoading} type="submit" className={styles.submitBtn}>
                                                Next
                                                {loginLoading && <SpinnerIos16Regular className="rotateLoader ms-1" />}
                                            </button>
                                            <span role="button" onClick={() => navigate('/forgot-password')} className={styles.secondaryBtnLink}>
                                                Forgot Password
                                            </span>
                                        </div>

                                        {/* Microsoft Login Button */}
                                        <div className={styles.microsoftLoginSection}>
                                            <div className={styles.divider}>
                                                <span className={styles.dividerText}>or</span>
                                            </div>
                                            <button type="button" onClick={handleMicrosoftLogin} disabled={microsoftLoading} className={styles.microsoftLoginBtn}>
                                                <i className="fab fa-microsoft" style={{ marginRight: '8px', color: '#00a4ef' }}></i>
                                                {microsoftLoading ? 'Connecting...' : 'Login with Microsoft'}
                                                {microsoftLoading && <SpinnerIos16Regular className="rotateLoader ms-1" />}
                                            </button>
                                        </div>

                                        <p className={styles.noAccountText}>
                                            Interested in Lens.{' '}
                                            <span role="button" onClick={() => (window.location.href = 'mailto:LensSupport@beyond-ma.com')} className={styles.secondaryBtnLink}>
                                                Request a demo.
                                            </span>
                                        </p>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <div className={styles.footer}>
                <a href="https://www.iubenda.com/terms-and-conditions/15531073" target="_blank" rel="noreferrer">
                    Terms of Use
                </a>
                <a href="https://www.iubenda.com/privacy-policy/15531073/legal?ifr=true&height=650&newmarkup=yes&an=no" target="_blank" rel="noreferrer">
                    Privacy & Cookies
                </a>
                <a
                    href="#cookie-preferences"
                    onClick={event => {
                        event.preventDefault();
                        openCookiePreferences();
                    }}>
                    Cookie preferences
                </a>
            </div>
        </div>
    );
}
export default Login;
